import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { base64FileDownload } from "../../utils/FileDownloadUtil";
import HasPermissionTo from "../../common/HasPermissionTo";
import { withTranslation } from "react-i18next";
import NscBusinessSettings from "./NscBusinessSettings";
import DealerBusinessSettings from "./DealerBusinessSettings";
import TermsAndConditionsClient from "../../clients/TermsAndConditionsClient";
import PendingActionClient from "../../clients/PendingActionClient";
import { getMarketBrandPath } from "../../utils/marketBrandUtils";
import {
  CUPID_ID,
  DELIVERY_BANDS,
  LEAD_TIMES,
  PAYMENT_THRESHOLD,
  TERMS_AND_CONDITIONS,
} from "../../common/Constants";

const findDealerByCommonId = (dealerGroup, commonId) => {
  if (!Array.isArray(dealerGroup)) {
    return undefined;
  }
  for (const dealer of dealerGroup) {
    if (dealer.commonId === commonId) {
      return dealer;
    }
  }
  return undefined;
};

const getDealersByBrand = (dealerGroup, brand) => {
  if (!Array.isArray(dealerGroup)) {
    return [];
  }
  const dealers = [];
  for (const dealer of dealerGroup) {
    if (dealer.commonId.endsWith(brand)) {
      dealers.push(dealer);
    }
  }
  return dealers;
};

const getOnboardedDealers = (dealers) => {
  if (!Array.isArray(dealers)) {
    return [];
  }
  const onboardedDealers = [];
  for (const dealer of dealers) {
    if (dealer.isOnboarded) {
      onboardedDealers.push(dealer);
    }
  }
  return onboardedDealers;
};

const getFirstPendingTerms = (termsHistory) => {
  if (!Array.isArray(termsHistory)) {
    return undefined;
  }
  for (const terms of termsHistory) {
    if (!terms.accepted) {
      return terms;
    }
  }
  return undefined;
};

const removeTermsByVersion = (termsHistory, version) => {
  if (!Array.isArray(termsHistory)) {
    return [];
  }
  const nextTermsHistory = [];
  for (const terms of termsHistory) {
    if (terms.version !== version) {
      nextTermsHistory.push(terms);
    }
  }
  return nextTermsHistory;
};

const BusinessSettings = (props) => {
  const [isDealerAcceptingTerms, setIsDealerAcceptingTerms] = useState(false);
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [termsAndConditionsHistory, setTermsAndConditionsHistory] = useState(
    []
  );
  const [enableUpload, setEnableUpload] = useState(undefined);
  const [restrictDropdown, setRestrictDropdown] = useState(false);
  const [fromPending, setFromPending] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const pendingAction = props.location.query?.fromPendingActions;

  const filterDealers = () => {
    const filteredDealers = [];
    if (props.dealerGroup) {
      const currentDealer = findDealerByCommonId(
        props.dealerGroup,
        props.user.commonId
      );
      if (
        props.user.showAll &&
        currentDealer &&
        (currentDealer.dealerType === "HUB" ||
          currentDealer.dealerType === "MDLR")
      ) {
        filteredDealers.push(
          ...getDealersByBrand(props.dealerGroup, props.user.brand)
        );
      } else if (currentDealer) {
        filteredDealers.push(currentDealer);
      }
    }
    return getOnboardedDealers(filteredDealers);
  };

  const filteredDealers = useMemo(
    () => filterDealers(),
    [props.dealerGroup, props.user]
  );

  useEffect(() => {
    let initialTab = 0;
    if (props.isNsc) {
      if (pendingAction && props.location.actionName === DELIVERY_BANDS) {
        initialTab = 1;
      } else if (pendingAction && props.location.actionName === LEAD_TIMES) {
        initialTab = 3;
      } else if (pendingAction && props.location.actionName === CUPID_ID) {
        initialTab = 2;
      } else if (
        pendingAction &&
        props.location.actionName === PAYMENT_THRESHOLD
      ) {
        initialTab = 4;
      }
    }
    setSelectedTab(initialTab);

    if (
      pendingAction &&
      props.location.actionName === TERMS_AND_CONDITIONS &&
      props.user.businessSettingsPage === -1
    ) {
      props.user.updateBusinessSettingsTab(0);
      setFromPending(true);
    }

    setIsDataLoaded(true);
  }, []);

  const handleTermsAndConditionsDownload = (termsandconditions) => {
    base64FileDownload(
      termsandconditions.file,
      "application/pdf",
      `${
        props.user.market.toUpperCase() +
        " " +
        props.t("TermsandConditionsHistory.documentName") +
        termsandconditions.version
      }.pdf`
    );
  };

  const addTermsAndConditionsPA = () => {
    props.hideOrShow(true);
    PendingActionClient.addPA(props.user, TERMS_AND_CONDITIONS).finally(() => {
      props.hideOrShow(false);
    });
  };

  const loadTermsAndConditionsHistory = () => {
    props.hideOrShow(true);
    TermsAndConditionsClient.retrieveTermsAndConditionsHistory(props.user)
      .then((data) => {
        const safeData = Array.isArray(data) ? data : [];
        setTermsAndConditionsHistory(safeData);
        setEnableUpload(safeData.length <= 1);

        if (
          pendingAction &&
          props.location.actionName === "TERMS_CONDITIONS" &&
          !props.isNsc &&
          props.user.businessSettingsPage === 0 &&
          fromPending
        ) {
          const tnC = getFirstPendingTerms(safeData);
          setIsDealerAcceptingTerms(true);
          setRestrictDropdown(true);
          setAcceptTermsAndConditions(tnC);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsDataLoaded(true);
        props.hideOrShow(false);
      });
  };

  const resetFromPending = () => {
    setFromPending(false);
    setIsDealerAcceptingTerms(false);
    setRestrictDropdown(false);
  };

  const alterComponentView = (termsAndConditions) => {
    setIsDealerAcceptingTerms((prev) => !prev);
    setAcceptTermsAndConditions(termsAndConditions);
    setRestrictDropdown((prev) => !prev);
    if (fromPending && props.history?.push) {
      props.history.push(
        props.user?.userType === "na_dealer"
          ? getMarketBrandPath(
              props.user.market,
              props.user.brand,
              "/dashboard"
            )
          : "/dashboard"
      );
    } else {
      loadTermsAndConditionsHistory();
    }
  };

  const deleteTerms = (version) => {
    props.hideOrShow(true);
    TermsAndConditionsClient.deleteTermsAndConditions(props.user, version)
      .then(() => {
        addTermsAndConditionsPA();
        setTermsAndConditionsHistory((prev) => {
          const next = removeTermsByVersion(prev, version);
          setEnableUpload(next.length <= 1);
          return next;
        });
      })
      .finally(() => {
        props.hideOrShow(false);
      });
  };

  const renderDealerBusinessSettings = () => (
    <DealerBusinessSettings
      {...props}
      startingTab={selectedTab}
      restrictDropdown={restrictDropdown}
      isDealerAcceptingTerms={isDealerAcceptingTerms}
      fromPending={fromPending}
      resetFromPending={resetFromPending}
      termsAndConditions={acceptTermsAndConditions}
      alterComponentView={alterComponentView}
      loadTermsAndConditionsHistory={loadTermsAndConditionsHistory}
      handleTermsAndConditionsDownload={handleTermsAndConditionsDownload}
      termsAndConditionsHistory={termsAndConditionsHistory}
      filteredDealers={filteredDealers}
      isDealer={true}
    />
  );

  const renderNscBusinessSettings = () => (
    <NscBusinessSettings
      {...props}
      startingTab={selectedTab}
      restrictDropdown={restrictDropdown}
      handleTermsAndConditionsDownload={handleTermsAndConditionsDownload}
      isDealer={false}
      enableUpload={enableUpload}
      deleteTerms={deleteTerms}
      loadTermsAndConditionsHistory={loadTermsAndConditionsHistory}
      termsAndConditionsHistory={termsAndConditionsHistory}
    />
  );

  if (isDataLoaded) {
    return (
      <div>
        <HasPermissionTo
          perform={["dealerBusinessSettings:view"]}
          permissions={props.user.permissions.rolePermissions}
          render={renderDealerBusinessSettings}
          noRender={renderNscBusinessSettings}
        />
      </div>
    );
  }
  return null;
};

BusinessSettings.propTypes = {
  dealerGroup: PropTypes.array,
  hideOrShow: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  isNsc: PropTypes.bool,
  location: PropTypes.shape({
    actionName: PropTypes.string,
    query: PropTypes.object,
  }).isRequired,
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    brand: PropTypes.string,
    businessSettingsPage: PropTypes.number,
    commonId: PropTypes.string,
    market: PropTypes.string,
    permissions: PropTypes.shape({
      rolePermissions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    }),
    showAll: PropTypes.bool,
    updateBusinessSettingsTab: PropTypes.func,
  }).isRequired,
  userDetails: PropTypes.object,
};

export default withTranslation("emp")(BusinessSettings);
